import { useState } from "react";
import { Radar } from "lucide-react";
import { Button, Card, CardTitle } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";

// Available-tech dispatch, office side: who raised their hand within THEIR
// drive radius of this job? One tap pings them with the job offer.

interface AvailTech {
  uid: string;
  displayName: string;
  distanceMi: number;
  radiusMi: number;
  expiresAt: number | null;
}

export function AvailableTechs({
  tenantId,
  jobId,
  lat,
  lng,
}: {
  tenantId: string;
  jobId: string;
  lat: number;
  lng: number;
}) {
  const [techs, setTechs] = useState<AvailTech[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [pinged, setPinged] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");

  async function search() {
    setBusy(true);
    setError("");
    try {
      const res = await call<{ techs: AvailTech[] }>(
        "listAvailableTechs",
        { tenantId, lat, lng },
        { mutation: false },
      );
      setTechs(res.techs);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function ping(uid: string) {
    setError("");
    try {
      await call("pingAvailableTech", { tenantId, uid, jobId });
      setPinged((prev) => new Set(prev).add(uid));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Available nearby</CardTitle>
        <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy} onClick={() => void search()}>
          <Radar size={14} /> {techs === null ? "Who's available?" : "Refresh"}
        </Button>
      </div>
      <p className="mt-1 text-xs text-steel">
        Off-shift techs who marked themselves available and whose drive radius reaches this job.
      </p>
      {techs !== null ? (
        <div className="mt-2">
          {techs.map((t) => (
            <div key={t.uid} className="flex items-center justify-between gap-2 border-b border-steel/10 py-1.5 last:border-0">
              <span className="min-w-0 truncate text-sm text-ink">
                {t.displayName}
                <span className="ml-1 text-xs text-steel">
                  {t.distanceMi} mi away · drives up to {t.radiusMi} mi
                  {t.expiresAt
                    ? ` · until ${new Date(t.expiresAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`
                    : ""}
                </span>
              </span>
              <Button
                className="shrink-0 px-2.5 py-1 text-xs"
                disabled={pinged.has(t.uid)}
                onClick={() => void ping(t.uid)}
              >
                {pinged.has(t.uid) ? "Pinged ✓" : "Ping"}
              </Button>
            </div>
          ))}
          {techs.length === 0 ? (
            <p className="py-1 text-xs text-steel">Nobody's available within range of this job right now.</p>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </Card>
  );
}
